import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Heart, Moon, Eye, ShieldAlert, Activity,
  Lock, ShieldCheck, AlertCircle, Save, ChevronDown, ChevronUp, Check, X
} from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { ref, uploadBytes } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { storage } from '../lib/firebase';
import { CARE_SUPPLEMENT } from '../data/care-supplement';

// Icon per group id (purely presentational)
const GROUP_ICONS = {
  comfort: Heart,
  sensory: Eye,
  bedtime: Moon,
  wellbeing: ShieldAlert,
  expression: Activity
};

// Representative authorization acknowledgments (legal-approved wording).
const REP_ACKNOWLEDGMENTS = [
  'This information is accessible only to authorized care staff and the family members I designate — never on public displays, the public directory, or automated summaries.',
  "I understand this is voluntary and that the resident's care will not be conditioned on providing it.",
  'I may revoke this authorization in writing at any time; revocation applies going forward.',
  'I may request correction or deletion of this information at any time.',
  'This authorization remains in effect while the resident resides at the community, unless revoked.',
  'Information disclosed to family may no longer be protected by HIPAA once they receive it.'
];

/**
 * Care & Comfort Supplement — staff/family-completed care-planning form.
 * Loads/saves via Cloud Functions (sensitive PHI is never client-readable via
 * Firestore rules). Saving is gated on recorded care-comfort consent.
 */
export default function CareSupplementScreen() {
  const navigate = useNavigate();
  const { communityId, storyId } = useParams();
  const functions = getFunctions();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [residentName, setResidentName] = useState('this resident');
  const [consent, setConsent] = useState({ granted: false });

  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [showConsent, setShowConsent] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const load = async () => {
    try {
      const loadFn = httpsCallable(functions, 'loadCareSupplement');
      const result = await loadFn({ storyId });
      setResidentName(result.data.residentName || 'this resident');
      setConsent(result.data.consent || { granted: false });
      setAnswers(result.data.answers || {});
    } catch (err) {
      console.error('Error loading care supplement:', err);
      setError(err.code === 'functions/permission-denied'
        ? 'You are not authorized to view this resident.'
        : 'Failed to load the care supplement.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId]);

  const consentGranted = consent?.granted === true;

  const setAnswer = (fieldId, value) => {
    setAnswers(prev => ({ ...prev, [fieldId]: value }));
    setSavedAt(null);
  };

  const toggleReveal = (groupId) => {
    setRevealed(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const handleRevoke = async () => {
    if (!window.confirm(`Revoke care-comfort consent for ${residentName}? Saving will be disabled until consent is recorded again. Existing notes are kept.`)) {
      return;
    }
    setRevoking(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'revokeCareComfortConsent');
      await fn({ storyId });
      await load();
    } catch (err) {
      console.error('Error revoking consent:', err);
      setError(err.message || 'Failed to revoke consent.');
    } finally {
      setRevoking(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const saveFn = httpsCallable(functions, 'saveCareSupplement');
      await saveFn({ storyId, answers });
      setSavedAt(Date.now());
    } catch (err) {
      console.error('Error saving care supplement:', err);
      setError(err.code === 'functions/failed-precondition'
        ? 'Consent is required before saving. Please record consent first.'
        : (err.message || 'Failed to save.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-quiltt-purple border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading…</p>
        </div>
      </div>
    );
  }

  if (error && !consentGranted && Object.keys(answers).length === 0 && residentName === 'this resident') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-gray-900 mb-2">{error}</h2>
          <button onClick={() => navigate(-1)} className="text-quiltt-purple hover:underline">Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(communityId ? `/community/${communityId}/resident/${storyId}` : -1)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-gray-900 truncate">{CARE_SUPPLEMENT.title}</h1>
            <p className="text-sm text-gray-500 truncate">For {residentName} · {CARE_SUPPLEMENT.description}</p>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-4">

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Consent gate */}
        {consentGranted ? (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800 flex items-center justify-between gap-3">
            <span className="flex items-start gap-2 min-w-0">
              <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                Care-comfort consent on file
                {consent.basis ? ` — ${consent.basis === 'representative' ? 'representative authorization' : 'resident notice'}` : ''}
                {consent.grantedByName ? ` (recorded by ${consent.grantedByName})` : ''}.
              </span>
            </span>
            <button
              onClick={handleRevoke}
              disabled={revoking}
              className="text-xs text-green-700 hover:text-green-900 underline flex-shrink-0 disabled:opacity-50"
            >
              {revoking ? 'Revoking…' : 'Revoke'}
            </button>
          </div>
        ) : (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <Lock className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900">Care-comfort consent not yet recorded</p>
              <p className="text-sm text-amber-800 mt-1">
                This supplement collects sensitive care information about a vulnerable
                person. Saving stays disabled until consent (resident notice or a
                personal-representative authorization) is on file for {residentName}.
              </p>
              <button
                onClick={() => setShowConsent(true)}
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-lg"
              >
                <ShieldCheck className="w-4 h-4" />
                Record consent
              </button>
            </div>
          </div>
        )}

        {/* Groups */}
        {CARE_SUPPLEMENT.groups.map(group => {
          const Icon = GROUP_ICONS[group.id] || Heart;
          const isSensitive = !!group.sensitive;
          const isOpen = !isSensitive || revealed[group.id];

          return (
            <section
              key={group.id}
              className={`bg-white rounded-xl shadow-sm border overflow-hidden ${
                isSensitive ? 'border-amber-200' : 'border-gray-100'
              }`}
            >
              {/* Group header */}
              <div className={`px-5 py-4 flex items-center gap-3 ${isSensitive ? 'bg-amber-50/40' : ''}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  isSensitive ? 'bg-amber-100' : 'bg-quiltt-50'
                }`}>
                  <Icon className={`w-5 h-5 ${isSensitive ? 'text-amber-600' : 'text-quiltt-purple'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-semibold text-gray-900">{group.title}</h2>
                  {isSensitive && (
                    <span className="inline-flex items-center gap-1 mt-0.5 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                      <ShieldAlert className="w-3 h-3" />
                      Sensitive · excluded from AI, family &amp; public
                    </span>
                  )}
                </div>
                {isSensitive && (
                  <button
                    onClick={() => toggleReveal(group.id)}
                    className="flex items-center gap-1.5 text-sm text-amber-700 hover:text-amber-800 font-medium flex-shrink-0"
                  >
                    {isOpen ? (<><ChevronUp className="w-4 h-4" /> Hide</>) : (<><ChevronDown className="w-4 h-4" /> Show fields</>)}
                  </button>
                )}
              </div>

              {/* Sensitive: collapsed explainer */}
              {isSensitive && !isOpen && (
                <div className="px-5 pb-5 -mt-1">
                  <p className="text-sm text-gray-500">
                    These fields hold sensitive personal health information. Open them
                    only to record what is necessary for {residentName}'s care.
                  </p>
                </div>
              )}

              {/* Fields */}
              {isOpen && (
                <div className="border-t border-gray-100 divide-y divide-gray-50">
                  {group.fields.map(field => (
                    <div key={field.id} className="px-5 py-4">
                      <label htmlFor={field.id} className="block text-sm font-medium text-gray-800">
                        {field.prompt}
                        {field.heightened && (
                          <span className="ml-2 text-xs font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded">
                            Heightened sensitivity
                          </span>
                        )}
                      </label>
                      {field.hint && (
                        <p className="text-xs text-gray-400 mt-0.5">{field.hint}</p>
                      )}
                      <textarea
                        id={field.id}
                        rows={2}
                        value={answers[field.id] || ''}
                        onChange={(e) => setAnswer(field.id, e.target.value)}
                        disabled={!consentGranted}
                        placeholder={consentGranted ? 'Add a note…' : 'Consent required to record'}
                        className="mt-2 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-purple/30 focus:border-quiltt-purple outline-none resize-y disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                      />
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <p className="text-xs text-gray-400">
            {savedAt
              ? 'Saved.'
              : consentGranted
                ? 'Visible to authorized care staff only.'
                : 'Saving disabled until consent is recorded.'}
          </p>
          <button
            onClick={handleSave}
            disabled={!consentGranted || saving}
            title={consentGranted ? 'Save' : 'Consent required'}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg bg-quiltt-purple text-white hover:bg-purple-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {saving
              ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving…</>
              : <><Save className="w-4 h-4" /> Save Supplement</>}
          </button>
        </div>
      </div>

      {/* Consent dialog */}
      {showConsent && (
        <RecordConsentDialog
          storyId={storyId}
          residentName={residentName}
          onClose={() => setShowConsent(false)}
          onRecorded={async () => {
            setShowConsent(false);
            setLoading(true);
            await load();
            setLoading(false);
          }}
        />
      )}
    </div>
  );
}


// ============================================
// Consent capture dialog
// ============================================

function RecordConsentDialog({ storyId, residentName, onClose, onRecorded }) {
  const functions = getFunctions();
  const [path, setPath] = useState(null); // 'resident_notice' | 'representative'
  const [relationship, setRelationship] = useState('');
  const [acks, setAcks] = useState(REP_ACKNOWLEDGMENTS.map(() => false));
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const sigRef = useRef(null);

  const allAcked = acks.every(Boolean);

  const recordResidentNotice = async () => {
    setSubmitting(true);
    setErr(null);
    try {
      const fn = httpsCallable(functions, 'recordCareComfortConsent');
      await fn({ storyId, basis: 'resident_notice' });
      await onRecorded();
    } catch (e) {
      console.error(e);
      setErr(e.message || 'Failed to record consent.');
      setSubmitting(false);
    }
  };

  const recordRepresentative = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      setErr('A signature is required for a representative authorization.');
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      // Upload signature
      const dataUrl = sigRef.current.toDataURL('image/png');
      const blob = await (await fetch(dataUrl)).blob();
      const path = `life-stories/${storyId}/consent/care_comfort_${Date.now()}.png`;
      await uploadBytes(ref(storage, path), blob);

      const fn = httpsCallable(functions, 'recordCareComfortConsent');
      await fn({
        storyId,
        basis: 'representative',
        relationship: relationship.trim(),
        acknowledged: true,
        signatureUrl: path
      });
      await onRecorded();
    } catch (e) {
      console.error(e);
      setErr(e.message || 'Failed to record authorization.');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <h2 className="font-semibold text-gray-900">Record Care-Comfort Consent</h2>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {err && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{err}</div>
          )}

          {!path && (
            <>
              <p className="text-sm text-gray-600">
                How is consent for {residentName} being recorded?
              </p>
              <button
                onClick={() => setPath('resident_notice')}
                className="w-full text-left p-4 rounded-xl border border-gray-200 hover:border-quiltt-purple/40 hover:bg-quiltt-50/40 transition-colors"
              >
                <p className="font-medium text-gray-900">Resident notice & agreement</p>
                <p className="text-sm text-gray-500 mt-0.5">The resident has capacity and agreed after being read the notice.</p>
              </button>
              <button
                onClick={() => setPath('representative')}
                className="w-full text-left p-4 rounded-xl border border-gray-200 hover:border-quiltt-purple/40 hover:bg-quiltt-50/40 transition-colors"
              >
                <p className="font-medium text-gray-900">Personal-representative authorization</p>
                <p className="text-sm text-gray-500 mt-0.5">A POA / legal guardian authorizes on behalf of a resident who lacks capacity. Requires a signature.</p>
              </button>
            </>
          )}

          {path === 'resident_notice' && (
            <>
              <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-700 leading-relaxed">
                “To take good care of you, our care team sometimes keeps private notes about what keeps
                you comfortable — your routines, what soothes you, and anything that's hard for you.
                These notes stay with your care team and aren't shown on the community screens.
                Is that alright with you? You can ask us to stop or change them anytime.”
              </div>
              <p className="text-sm text-gray-600">
                Confirm that this notice was given to {residentName} and that they agreed.
              </p>
              <div className="flex items-center justify-between gap-3 pt-2">
                <button onClick={() => setPath(null)} className="text-sm text-gray-500 hover:text-gray-700">Back</button>
                <button
                  onClick={recordResidentNotice}
                  disabled={submitting}
                  className="flex items-center gap-2 bg-quiltt-purple hover:bg-purple-700 disabled:bg-gray-300 text-white text-sm font-medium px-4 py-2 rounded-lg"
                >
                  {submitting ? 'Recording…' : <>Record resident agreement</>}
                </button>
              </div>
            </>
          )}

          {path === 'representative' && (
            <>
              <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-700 leading-relaxed">
                “I authorize the community to collect and use sensitive care information about {residentName}
                — comfort and sensory preferences, fears or anxieties, experiences relevant to their care,
                and related health or behavioral notes — for the purpose of care planning and daily care.”
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your relationship to {residentName}</label>
                <input
                  type="text"
                  value={relationship}
                  onChange={(e) => setRelationship(e.target.value)}
                  placeholder="e.g., Daughter (POA), Legal Guardian"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-purple/30 focus:border-quiltt-purple outline-none"
                />
              </div>

              <div className="space-y-2">
                {REP_ACKNOWLEDGMENTS.map((text, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setAcks(prev => prev.map((v, j) => (j === i ? !v : v)))}
                    className="w-full flex items-start gap-2.5 text-left"
                  >
                    <span className={`w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      acks[i] ? 'bg-quiltt-purple border-quiltt-purple' : 'border-gray-300'
                    }`}>
                      {acks[i] && <Check className="w-3 h-3 text-white" />}
                    </span>
                    <span className="text-sm text-gray-700">{text}</span>
                  </button>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Signature</label>
                <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden">
                  <SignatureCanvas
                    ref={sigRef}
                    canvasProps={{ className: 'signature-canvas w-full h-40' }}
                    backgroundColor="white"
                  />
                </div>
                <button
                  onClick={() => sigRef.current?.clear()}
                  className="text-xs text-gray-500 hover:text-gray-700 underline mt-1"
                >
                  Clear signature
                </button>
              </div>

              <div className="flex items-center justify-between gap-3 pt-2">
                <button onClick={() => setPath(null)} className="text-sm text-gray-500 hover:text-gray-700">Back</button>
                <button
                  onClick={recordRepresentative}
                  disabled={submitting || !relationship.trim() || !allAcked}
                  className="flex items-center gap-2 bg-quiltt-purple hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 rounded-lg"
                >
                  {submitting ? 'Authorizing…' : 'Authorize'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
